import type { ReactNode } from 'react';
import { Checkbox, FormControlLabel } from '@mui/material';

import { createDataTestId } from '@/utils/createDataTestId';

import { useLeaseField } from '../../hooks/use-lease-field';

export type LeaseCheckboxInputProps = {
  name: string;
  label?: ReactNode;
  disabled?: boolean;
  /** Checked-state color; omit to keep the default MUI primary. */
  checkedColor?: string;
  onChangeValue?: (checked: boolean) => void;
};

// Boolean checkbox bound to RHF via `useLeaseField`. Stores a boolean in form
// state (unlike the string-valued primitives).
export function LeaseCheckboxInput({
  name,
  label,
  disabled = false,
  checkedColor,
  onChangeValue,
}: LeaseCheckboxInputProps) {
  const field = useLeaseField<boolean | null | undefined>(name);
  const checked = Boolean(field.value);
  const dataTestId = createDataTestId({ inputString: name, suffix: 'checkbox' });

  const handleChange = (_event: React.ChangeEvent<HTMLInputElement>, nextChecked: boolean) => {
    field.onChange(nextChecked);
    onChangeValue?.(nextChecked);
  };

  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={checked}
          onChange={handleChange}
          onBlur={field.onBlur}
          inputRef={field.ref}
          disabled={disabled}
          color="primary"
          id={dataTestId}
          data-testid={dataTestId}
          // Only override when a color is provided — otherwise render the default primary unchanged.
          sx={checkedColor ? { '&.Mui-checked': { color: checkedColor } } : undefined}
        />
      }
      label={label ?? ''}
    />
  );
}
